/**
 * Makro-Verwaltung (Spec 8.2) — Liste plus ein Formular, das Aktionen
 * **in Reihenfolge** zusammenstellt.
 *
 * Bewusst kein Drag-and-drop: Aktionen werden hinten angehaengt und einzeln
 * entfernt. Bei drei bis fuenf Schritten je Makro ist das schneller als jede
 * Sortier-Mechanik, und es gibt nichts, was auf einem Touchpad kaputtgehen
 * kann.
 */
import {
  MACRO_ACTION_TYPES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  type Macro,
  type MacroAction,
  type MacroActionType,
  type Snippet,
  type Tag,
  type Team,
  type TenantMemberDetail,
} from '@tickethub/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

type MacroEditorProps = {
  macros: Macro[];
  tags: Tag[];
  snippets: Snippet[];
  members: TenantMemberDetail[];
  teams: Team[];
  readOnly: boolean;
  onCreate: (input: { title: string; description: string; actions: MacroAction[] }) => Promise<void>;
  onDelete: (macroId: string) => Promise<void>;
};

/** Kurzfassung einer Aktion fuer die Liste — eine Zeile, kein Formular. */
function describe(action: MacroAction, t: (key: string) => string): string {
  switch (action.type) {
    case 'set_status':
      return `${t('macro.action_set_status')}: ${t(`ticket.status_${action.status}`)}`;
    case 'set_priority':
      return `${t('macro.action_set_priority')}: ${t(`ticket.priority_${action.priority}`)}`;
    case 'assign':
      return t('macro.action_assign');
    case 'add_tag':
      return t('macro.action_add_tag');
    case 'insert_snippet':
      return t('macro.action_insert_snippet');
    case 'create_skilllock_task':
      return t('macro.action_create_skilllock_task');
  }
}

export function MacroEditor({
  macros,
  tags,
  snippets,
  members,
  teams,
  readOnly,
  onCreate,
  onDelete,
}: MacroEditorProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [actions, setActions] = useState<MacroAction[]>([]);
  const [type, setType] = useState<MacroActionType>('set_status');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  /** Baut aus Typ + Auswahl die Aktion. `null` = Auswahl fehlt noch. */
  function buildAction(): MacroAction | null {
    switch (type) {
      case 'set_status':
        return value === '' ? null : { type, status: value as (typeof TICKET_STATUSES)[number] };
      case 'set_priority':
        return value === '' ? null : { type, priority: value as (typeof TICKET_PRIORITIES)[number] };
      case 'assign': {
        if (value === '') return null;
        const [kind, id] = value.split(':');
        return kind === 'team'
          ? { type, member_id: null, team_id: id ?? null }
          : { type, member_id: id ?? null, team_id: null };
      }
      case 'add_tag':
        return value === '' ? null : { type, tag_id: value };
      case 'insert_snippet':
        return value === '' ? null : { type, snippet_id: value };
      case 'create_skilllock_task':
        return { type, title: null, board_id: value === '' ? null : value };
    }
  }

  function addAction() {
    const action = buildAction();
    if (action === null) return;
    setActions((current) => [...current, action]);
    setValue('');
  }

  async function submit() {
    if (title.trim().length === 0 || actions.length === 0) return;
    setBusy(true);
    try {
      await onCreate({ title: title.trim(), description: description.trim(), actions });
      setTitle('');
      setDescription('');
      setActions([]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {!readOnly ? (
        <section className="space-y-3 rounded-lg border p-4">
          <h2 className="text-lg font-medium">{t('macro.new')}</h2>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="macro-title">{t('macro.name')}</Label>
              <Input
                id="macro-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="macro-description">{t('macro.description')}</Label>
              <Input
                id="macro-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-2">
              <Label htmlFor="macro-action-type">{t('macro.action')}</Label>
              <Select
                id="macro-action-type"
                value={type}
                onChange={(event) => {
                  setType(event.target.value as MacroActionType);
                  setValue('');
                }}
              >
                {MACRO_ACTION_TYPES.map((entry) => (
                  <option key={entry} value={entry}>
                    {t(`macro.action_${entry}`)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="macro-action-value">{t('macro.action_value')}</Label>
              {type === 'create_skilllock_task' ? (
                <Input
                  id="macro-action-value"
                  value={value}
                  placeholder={t('skilllock.default_board')}
                  onChange={(event) => setValue(event.target.value)}
                />
              ) : (
                <Select
                  id="macro-action-value"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                >
                  <option value="">{t('macro.choose')}</option>
                  {type === 'set_status'
                    ? TICKET_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {t(`ticket.status_${status}`)}
                        </option>
                      ))
                    : null}
                  {type === 'set_priority'
                    ? TICKET_PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {t(`ticket.priority_${priority}`)}
                        </option>
                      ))
                    : null}
                  {type === 'assign' ? (
                    <>
                      {members.map((member) => (
                        <option key={member.id} value={`member:${member.id}`}>
                          {member.user.name}
                        </option>
                      ))}
                      {teams.map((team) => (
                        <option key={team.id} value={`team:${team.id}`}>
                          {team.name}
                        </option>
                      ))}
                    </>
                  ) : null}
                  {type === 'add_tag'
                    ? tags.map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.name}
                        </option>
                      ))
                    : null}
                  {type === 'insert_snippet'
                    ? snippets.map((snippet) => (
                        <option key={snippet.id} value={snippet.id}>
                          {snippet.title}
                        </option>
                      ))
                    : null}
                </Select>
              )}
            </div>

            <Button type="button" variant="outline" onClick={addAction}>
              <Plus aria-hidden="true" />
              {t('macro.add_action')}
            </Button>
          </div>

          {actions.length > 0 ? (
            <ol className="space-y-1">
              {actions.map((action, index) => (
                <li key={`${action.type}-${index}`} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">{index + 1}</Badge>
                  {describe(action, t)}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={t('common.delete')}
                    onClick={() => setActions((current) => current.filter((_, i) => i !== index))}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-xs text-muted-foreground">{t('macro.no_actions')}</p>
          )}

          <Button
            type="button"
            disabled={busy || title.trim().length === 0 || actions.length === 0}
            onClick={() => void submit()}
          >
            {busy ? t('common.saving') : t('macro.create')}
          </Button>
        </section>
      ) : null}

      {macros.length === 0 ? (
        <Alert>
          <AlertDescription>{t('macro.empty')}</AlertDescription>
        </Alert>
      ) : (
        <ul className="space-y-2">
          {macros.map((macro) => (
            <li key={macro.id} className="flex items-start gap-3 rounded-lg border p-3">
              <div className="flex-1">
                <p className="font-medium">{macro.title}</p>
                {macro.description ? (
                  <p className="text-sm text-muted-foreground">{macro.description}</p>
                ) : null}
                <ol className="mt-1 flex flex-wrap gap-1">
                  {macro.actions.map((action, index) => (
                    <li key={`${macro.id}-${index}`}>
                      <Badge variant="outline">{describe(action, t)}</Badge>
                    </li>
                  ))}
                </ol>
              </div>
              {!readOnly ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.delete')}
                  onClick={() => void onDelete(macro.id)}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
